// Port of src/app/app-routing.module.ts (react-router v7, library mode).
//
// Route table mirrors the Angular Routes array:
// - '', 'similar', 'artist'        → MainContentComponent → <MainContent/>
// - 'playlist/:artist'             → TopPlaylistsComponent (route param passed
//                                    as artistFromRoute)
// - 'top-playlists'                → TopPlaylistsComponent
// - 'signup', 'explorer-signup',
//   'affiliate-program'            → MainContentComponent → <MainContent/>. In
//   production the urlrewrite serves these landing paths as the SPA index.html
//   with the URL kept on the landing path, so the root component mounted and its
//   ngOnInit opened the LOGIN_REGISTER / AFFILIATION_LINK modal with the inputs
//   derived from window.location (Overwolf mode, promocode, prefill, explorer,
//   affiliate). MainContent's `currentUrl.includes(...)` blocks reproduce this.
// - basename normalization: bare landing paths reach the app without the
//   '/songhunt/' prefix (urlrewrite keeps the browser URL bare); they are
//   canonicalized in place before the router initializes — see normalize-base-path.
// - ':path/' → '/:path' trailing-slash redirect: react-router matches paths
//   with or without a trailing slash, so the Angular redirect rule is
//   inherently satisfied and needs no explicit route.
// - '**': Angular had no wildcard route (commented out) — an unmatched URL
//   left the outlet empty while the app shell (nav bar etc.) still rendered.
//   Mirrored with a catch-all route whose element is null.
//
// NOTE(port): the Angular config also lazy-loaded NavBarModule / PipeModule /
// DirectiveModule / SharedModule under '/navbar', '/pipes', '/directives',
// '/shared'. Those modules declared no child routes (navigating there could
// never render anything) and were all eagerly imported by AppModule anyway —
// vestigial registrations, not ported. ProfileModule was likewise eagerly
// imported in AppModule (Profile is an app-shell overlay, not a route), so no
// React.lazy is used anywhere — nothing was effectively lazy in Angular.
//
// basename: import.meta.env.BASE_URL → '/' in dev, '/songhunt/' in the prod
// build (vite.config.ts `base`), matching Angular's --base-href=/songhunt/.

import { createBrowserRouter, useLocation } from 'react-router';
import App from '../App';
import MainContent from '../features/main-content/MainContent';
import OwSurvey from '../features/ow-survey/OwSurvey';
import OwCancel from '../features/ow-cancel/OwCancel';
import OwPlan from '../features/ow-plan/OwPlan';
import { applyBasePathNormalization } from './normalize-base-path';

/**
 * Angular's ActivatedRoute.queryParams subscription (inside MainContent)
 * re-emitted when the query string changed; MainContent re-runs
 * handleQueryParams when its routeKey prop changes — keyed on location.search.
 */
function MainContentRoute() {
  const location = useLocation();
  return <MainContent routeKey={location.search} />;
}

// NOTE(port): the /signup, /explorer-signup and /affiliate-program landing routes
// render MainContentRoute (NOT a thin modal-opening component). In production the
// urlrewrite serves these as the SPA's index.html and the browser keeps the
// landing path, so Angular mounted the root component (MainContent) whose ngOnInit
// sniffed window.location and opened the right modal WITH the right inputs —
// Overwolf-mode detection, promocode/affiliate extraction, e-mail/name/pass
// prefill, the "Sign Up for FREE"/onlyExplorer explorer variant, and the
// affiliation-link modal. MainContent's `currentUrl.includes('/signup' | ...)`
// blocks reproduce exactly that. An earlier port wired these to a tiny component
// that opened the modal with DEFAULT inputs, dropping all of the above — see the
// react-basename-bare-path-preloader-hang note.

// Bare landing paths (/signup/, /playlist/..., etc.) arrive without the
// '/songhunt/' base prefix the router's basename expects; canonicalize them in
// place before the history/router initialize so every route matches.
applyBasePathNormalization();

// Exported so tests can import the route config and run matchRoutes() against it
// without going through createBrowserRouter (which starts the history listener).
export const routes = [
  // The Overwolf uninstall survey is a SIBLING of the app shell, not a child of
  // it: the Overwolf uninstaller opens this page in the browser at the moment the
  // app is deleted, so it must render with no NavBar, no store bootstrap, no
  // session and no API dependency. Mounting it under <App/> would boot the whole
  // application behind a page whose entire premise is that the app is gone.
  // (It removes #app-preloader itself — AppLoader lives in the shell.)
  { path: '/ow-survey', element: <OwSurvey /> },

  // The Overwolf cancellation page is a sibling for the same reasons, plus one of
  // its own: it must never open the affiliate-link popup at a user who is already
  // trying to leave. Outside the shell there is no login, so the affiliation timer
  // (started only from login/auto-login) can never run here.
  { path: '/ow-cancel', element: <OwCancel /> },

  // The Overwolf change-plan page, opened from the app's menu with an SH1 blob
  // that identifies the user. Also a sibling: it signs its own user in from that
  // blob and brings its own ModalHost for the upgrade flow, so booting the shell
  // behind it would only add a second, conflicting session.
  { path: '/ow-plan', element: <OwPlan /> },
  {
    // App is the shell (port of app.component.html): NavBar / Playlist /
    // TopPlaylists overlay / AdminPanel / Profile / ModalHost / Toasts live
    // outside route switching; the routed content renders in its <Outlet/>.
    path: '/',
    element: <App />,
    children: [
      { index: true, element: <MainContentRoute /> },
      // 'similar'/'artist' and their deep-link variants
      // (/similar/<artist>-<title>, /artist/<name>) all mount MainContent so the
      // results grid renders. NavBar reads the trailing segment on mount and runs
      // the similar/artist search (see NavBar's `currentUrl.includes('/similar/')`).
      // Without the param routes the extra segment fell to the catch-all (empty
      // outlet) — the search ran but no results showed.
      { path: 'similar', element: <MainContentRoute /> },
      { path: 'similar/:song', element: <MainContentRoute /> },
      { path: 'artist', element: <MainContentRoute /> },
      { path: 'artist/:artist', element: <MainContentRoute /> },

      // Curated / top-playlists. The single full-screen <TopPlaylists> overlay is
      // rendered by App when view === 'curated' (derived from isCuratedPath), so
      // these routes must NOT render TopPlaylists themselves (that would
      // double-render it). They render MainContentRoute — the SAME element as the
      // index/results routes — so React keeps the MainContent instance mounted
      // across home↔curated navigation (no bootstrap re-run); it simply sits,
      // covered, behind the overlay. The :artist param was accepted-but-unused.
      { path: 'playlist/:artist', element: <MainContentRoute /> },
      { path: 'top-playlists', element: <MainContentRoute /> },
      { path: 'signup', element: <MainContentRoute /> },
      { path: 'explorer-signup', element: <MainContentRoute /> },
      { path: 'affiliate-program', element: <MainContentRoute /> },

      // Unmatched URLs: empty outlet, shell still renders (see header note).
      { path: '*', element: null },
    ],
  },
];

export const router = createBrowserRouter(routes, { basename: import.meta.env.BASE_URL });
